/**
 * Pure pagination core. Given a logical page's blocks — each measured for
 * height, and told whether it may break between internal items — it packs them
 * into physical sheets of a fixed content height, keeping atomic blocks whole
 * and breaking list blocks only between their items (never mid-item). Headers
 * stay with the block they introduce; the flex "grow" filler is dropped.
 *
 * This is the functional core: no DOM, no React. The measurement shell feeds it
 * real heights and renders the sheets it returns.
 */

/** A block measured and classified for pagination. */
export interface BlockBox {
  readonly key: string;
  /** Full measured height in px, including the margin gap above the block. */
  readonly height: number;
  /** `items` = may break between internal items; `none` = atomic, never split. */
  readonly split: 'none' | 'items';
  /** Per-item heights (split:'items' only). Each item is kept whole. */
  readonly itemHeights?: readonly number[];
  /** Height of the block's non-item chrome (a title, padding) — kept with the first slice. */
  readonly overhead?: number;
  /**
   * CSS columns the items flow into (split:'items' only; default 1). With 2+,
   * `itemHeights` are each item's own height and a slice is as tall as its
   * items balanced across the columns, not their sum.
   */
  readonly columns?: number;
  /** Keep this block on the same sheet as the block after it (e.g. a section header). */
  readonly keepWithNext?: boolean;
  /**
   * The flex filler that pins a footer to the sheet bottom — never placed; the
   * render layer re-adds it on the last sheet. Its measured height is really
   * the gap above the footer (the footer's top margin), so it is counted with
   * the block after it.
   */
  readonly isGrow?: boolean;
  /**
   * The page footer pinned to the sheet bottom. It may dip into the bottom
   * padding (see `footerAllowance`) and is never left alone on a sheet.
   */
  readonly isFooter?: boolean;
}

/** Where one block (or a slice of a splittable block) lands on a sheet. */
export type Placement =
  | { readonly kind: 'block'; readonly key: string }
  /** A slice of a split:'items' block: items [from, to). `head` renders the block's chrome/title. */
  | { readonly kind: 'slice'; readonly key: string; readonly from: number; readonly to: number; readonly head: boolean };

export interface Sheet {
  readonly placements: readonly Placement[];
  /** Px of the sheet's bottom padding the footer took to fit; the render layer trims the padding by this. */
  readonly bottomTrim?: number;
}

export interface PaginateOptions {
  /**
   * How far the page footer may dip into the sheet's bottom padding, in px.
   * A page a few px over keeps its footer instead of spilling onto a sheet
   * of its own.
   */
  readonly footerAllowance?: number;
}

/**
 * How tall `heights` stand when CSS balances them across `columns` columns:
 * the shortest height at which filling each column in order (items kept
 * whole, as `break-inside: avoid` does) uses no more than `columns` columns.
 * One column is the plain sum.
 */
export function balancedColumnHeight(heights: readonly number[], columns: number): number {
  const total = heights.reduce((sum, h) => sum + h, 0);
  if (columns <= 1 || heights.length <= 1) return total;
  const fits = (limit: number): boolean => {
    let used = 1;
    let filled = 0;
    for (const h of heights) {
      if (filled > 0 && filled + h > limit) {
        used += 1;
        filled = 0;
      }
      filled += h;
    }
    return used <= columns;
  };
  // Whole pixels: the smallest limit that fits, between the lower bound and the sum.
  let low = Math.ceil(Math.max(...heights, total / columns));
  let high = Math.ceil(total);
  while (low < high) {
    const mid = Math.floor((low + high) / 2);
    if (fits(mid)) high = mid;
    else low = mid + 1;
  }
  return low;
}

/** The first unbreakable chunk of a block — used to decide if a header can keep with it. */
function firstUnitHeight(block: BlockBox, contentHeight: number): number {
  if (block.split === 'items') {
    const items = block.itemHeights ?? [];
    return (block.overhead ?? 0) + (items.length > 0 ? (items[0] ?? 0) : 0);
  }
  // An atomic block taller than a sheet can never fully fit; cap so a header
  // isn't pushed off forever chasing an impossible fit.
  return Math.min(block.height, contentHeight);
}

/**
 * Pack `blocks` into sheets of `contentHeight` pixels.
 *
 * @param blocks - Top-level blocks in order, measured and classified.
 * @param contentHeight - Usable height of one sheet (sheet minus vertical padding), in px.
 * @returns One or more sheets; always at least one (possibly empty).
 */
export function paginatePage(blocks: readonly BlockBox[], contentHeight: number, options: PaginateOptions = {}): Sheet[] {
  const footerAllowance = Math.max(0, options.footerAllowance ?? 0);
  const sheets: Sheet[] = [];
  let current: Placement[] = [];
  let used = 0;
  /** What each placement on the current sheet took, so the last one can follow the footer. */
  let currentHeights: number[] = [];
  let bottomTrim = 0;
  /** The grow filler's gap, owed by the next block (the footer's top margin). */
  let carry = 0;

  const flush = (): void => {
    sheets.push(bottomTrim > 0 ? { placements: current, bottomTrim } : { placements: current });
    current = [];
    currentHeights = [];
    used = 0;
    bottomTrim = 0;
  };
  // Start a fresh sheet only if the current one already holds something.
  const breakSheet = (): void => {
    if (current.length > 0) flush();
  };

  const place = (placement: Placement, height: number): void => {
    current.push(placement);
    currentHeights.push(height);
    used += height;
  };

  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i]!;
    if (block.isGrow) {
      // Filler: the render layer re-adds it on the last sheet. Its gap belongs to the next block.
      carry += block.height;
      continue;
    }
    const owed = carry;
    carry = 0;

    if (block.isFooter) {
      const need = owed + block.height;
      if (used + need > contentHeight + footerAllowance && current.length > 1) {
        // Never a footer alone: the last thing on this sheet goes over with it.
        const last = current.pop()!;
        const lastHeight = currentHeights.pop()!;
        used -= lastHeight;
        flush();
        place(last, lastHeight);
      } else if (used + need > contentHeight + footerAllowance && current.length === 1 && used > 0) {
        breakSheet();
      }
      place({ kind: 'block', key: block.key }, need);
      bottomTrim = Math.max(0, Math.ceil(used - contentHeight));
      continue;
    }

    if (block.split === 'items' && block.itemHeights && block.itemHeights.length > 0) {
      const items = block.itemHeights;
      const overhead = block.overhead ?? 0;
      const columns = Math.max(1, Math.floor(block.columns ?? 1));
      // Items [from, to) as they stand on the sheet: summed, or balanced across columns.
      const sliceHeight = (from: number, to: number): number => balancedColumnHeight(items.slice(from, to), columns);
      let idx = 0;
      let head = true;
      while (idx < items.length) {
        // Make sure at least the chrome (first slice) + one item can fit here.
        const need = (head ? overhead : 0) + (items[idx] ?? 0);
        if (used + need > contentHeight && used > 0) breakSheet();

        const from = idx;
        const chrome = head ? overhead : 0;
        while (idx < items.length && used + chrome + sliceHeight(from, idx + 1) <= contentHeight) {
          idx++;
        }
        if (idx === from) {
          // Not even one item fit. On a fresh sheet, force one (oversized item
          // overflows rather than looping forever); otherwise try a new sheet.
          if (used === 0) {
            idx++;
          } else {
            breakSheet();
            continue;
          }
        }
        const consumed = chrome + sliceHeight(from, idx);
        place({ kind: 'slice', key: block.key, from, to: idx, head }, consumed);
        head = false;
        if (idx < items.length) breakSheet();
      }
      continue;
    }

    // Atomic block.
    let need = owed + block.height;
    const next = blocks[i + 1];
    if (block.keepWithNext && next && !next.isGrow) {
      need += firstUnitHeight(next, contentHeight);
    }
    if (used + need > contentHeight && used > 0) breakSheet();
    place({ kind: 'block', key: block.key }, owed + block.height);
  }

  flush();
  return sheets;
}
